import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/Auth/authContext';
import { useI18n } from '../../../contexts/I18nContext';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { rolesForSpace } from '../../../utils/roleCatalog';
import { userService } from '../../../services/userService';
import { clientService } from '../../../services/clientService';
import { Client } from '../../../types/client';
import { UserGroup, UserRole, UserUpdate, UserWithDetails } from '../../../types/user';
import config from '../../../config/env';
import { generatePassword } from '../passwordTools';
import { canManageRoleAtLevel, clientIdFromGroupPath, highestRoleLevel } from '../roleHierarchy';

export type UserDetailTab = 'details' | 'groups' | 'roles' | 'password';

/** The API's error detail when there is one, else the message, else a fallback. */
const errorText = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (detail) return typeof detail === 'string' ? detail : JSON.stringify(detail);
  return err?.message || fallback;
};

/**
 * Everything the user dialog shows and does: the identity form, the
 * client groups, the roles on this environment, the password reset and
 * the deletion. Each tab reads the slice it needs from this state.
 */
export const useUserDetail = (user: UserWithDetails | null, isOpen: boolean, onClose: () => void, onUserUpdated: () => void) => {
  const { user: currentUser } = useAuth();
  const { t } = useI18n();
  // Mirrors require_user_write on the API: an ANEP Admin reads the
  // directory but never changes it, so every write control is hidden.
  const { canWriteUsers, isSuperAdmin: callerIsSuperAdmin, isAdmin: callerIsAdmin } = useClientAccess();
  // A customer's admin manages people, not Keycloak plumbing: group
  // membership and internal ids are ANEP concerns, hidden from them.
  const showsInternals = callerIsSuperAdmin || callerIsAdmin;
  const isSuperAdmin = useMemo(() => currentUser?.roles?.includes('SuperAdmin') || false, [currentUser?.roles]);
  const callerLevel = useMemo(() => highestRoleLevel(currentUser?.roles), [currentUser?.roles]);
  // Roles are client roles on provisioning-{env}: the environment this frontend serves.
  const environmentClientId = config.oauth.clientId;

  // Dialog
  const [activeTab, setActiveTab] = useState<UserDetailTab>('details');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Identity form
  const [isEditing, setIsEditing] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEnabled, setEditEnabled] = useState(true);
  const [editEmailVerified, setEditEmailVerified] = useState(false);

  // Groups
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [clientNames, setClientNames] = useState<Map<string, string>>(new Map());
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [selectedClientToAdd, setSelectedClientToAdd] = useState('');
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  // Roles
  const [clientRoles, setClientRoles] = useState<UserRole[]>([]);
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState('');
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  // Password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const seedForm = useCallback((u: UserWithDetails) => {
    setEditEmail(u.email || '');
    setEditFirstName(u.firstName || '');
    setEditLastName(u.lastName || '');
    setEditEnabled(u.enabled);
    setEditEmailVerified(u.emailVerified);
  }, []);

  const fetchGroups = useCallback(async (userId: string) => {
    try {
      const userGroups = await userService.getUserGroups(userId);
      setGroups(userGroups);
      // The client groups carry the client UUID in their path: resolve the names.
      const uuids = new Set<string>();
      userGroups.forEach((group) => {
        const id = clientIdFromGroupPath(group.path);
        if (id) uuids.add(id);
      });
      if (uuids.size > 0) {
        const names = new Map<string, string>();
        for (const uuid of uuids) names.set(uuid, await clientService.getClientName(uuid));
        setClientNames(names);
      }
    } catch (err) {
      console.error('[USER DETAIL] Failed to fetch groups:', err);
      setGroups([]);
    }
  }, []);

  const fetchRoles = useCallback(async (userId: string) => {
    setIsLoadingRoles(true);
    try {
      const response = await userService.getUserClientRoles(userId, environmentClientId);
      setClientRoles(response.roles || []);
    } catch (err) {
      console.error('[USER DETAIL] Failed to fetch client roles:', err);
      setClientRoles([]);
      setError(t('users.detail.errRolesLoad'));
    } finally {
      setIsLoadingRoles(false);
    }
  }, [environmentClientId, t]);

  // A new user: seed the form, load the groups (and the clients a SuperAdmin may add).
  useEffect(() => {
    if (!user) return;
    seedForm(user);
    setError(null);
    setSuccess(null);
    fetchGroups(user.id);
    if (isSuperAdmin) clientService.fetchActiveClients().then(setAllClients).catch(console.error);
  }, [user, isSuperAdmin, seedForm, fetchGroups]);

  // The roles load when their tab opens.
  useEffect(() => {
    if (activeTab === 'roles' && user) fetchRoles(user.id);
  }, [activeTab, user, fetchRoles]);

  // The space the account lives in, read from its groups: a client's member
  // only takes ClientAdmin or Viewer, an ANEP account the ANEP roles.
  const targetSpace = useMemo<'anep' | 'client'>(() => {
    if (groups.some((g) => g.path.startsWith('/Clients/'))) return 'client';
    if (groups.some((g) => g.path === '/Anep' || g.path.startsWith('/Anep/'))) return 'anep';
    return 'client';
  }, [groups]);

  /** Roles this caller may hand out: the account's catalogue, never above the caller's level. */
  const availableRoles = useMemo(() => {
    const catalogue = rolesForSpace(targetSpace).map((r) => r.id);
    return catalogue.filter((id) => canManageRoleAtLevel(id, callerLevel, isSuperAdmin));
  }, [targetSpace, isSuperAdmin, callerLevel]);

  const canManageRole = (roleName: string): boolean => canWriteUsers && canManageRoleAtLevel(roleName, callerLevel, isSuperAdmin);

  /** The catalogue's wording for a role, when Keycloak carries none. */
  const roleDescription = (name: string, fromKeycloak?: string): string => {
    if (fromKeycloak) return fromKeycloak;
    const key = `users.roleDescriptions.${name}` as const;
    const text = t(key as never);
    return text && text !== key ? text : '';
  };

  /** Clients the user is not a member of yet. */
  const availableClientsToAdd = useMemo(() => {
    const memberOf = new Set(groups.map((g) => clientIdFromGroupPath(g.path)).filter(Boolean));
    return allClients.filter((client) => !memberOf.has(client.id));
  }, [groups, allClients]);

  // ------------------------------------------------------------- actions
  const saveIdentity = async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);
    try {
      const updateData: UserUpdate = {
        email: editEmail || undefined,
        firstName: editFirstName || undefined,
        lastName: editLastName || undefined,
        enabled: editEnabled,
        emailVerified: editEmailVerified,
      };
      await userService.updateUser(user.id, updateData);
      setSuccess(t('users.detail.msgUpdated'));
      setIsEditing(false);
      setTimeout(onUserUpdated, 1000);
    } catch (err: any) {
      console.error('[USER DETAIL] Error updating user:', err);
      setError(errorText(err, t('users.detail.errUpdate')));
    } finally {
      setIsLoading(false);
    }
  };

  const cancelEdit = () => {
    if (user) seedForm(user);
    setIsEditing(false);
  };

  const deleteUser = async () => {
    if (!user) return;
    if (!window.confirm(t('users.deleteUserConfirm', { name: user.username }))) return;
    setIsLoading(true);
    setError(null);
    try {
      await userService.deleteUser(user.id);
      setSuccess(t('users.detail.msgDeleted'));
      setTimeout(onUserUpdated, 1000);
    } catch (err: any) {
      console.error('[USER DETAIL] Error deleting user:', err);
      setError(errorText(err, t('users.detail.errDelete')));
    } finally {
      setIsLoading(false);
    }
  };

  const addClientGroup = async () => {
    if (!user || !selectedClientToAdd) return;
    setIsLoadingGroups(true);
    setError(null);
    try {
      const groupInfo = await clientService.getClientGroup(selectedClientToAdd);
      if (!groupInfo) {
        setError(t('users.detail.errGroupAdd'));
        return;
      }
      await userService.addUserToGroup(user.id, groupInfo.id);
      setSuccess(t('users.detail.msgGroupAdded'));
      setSelectedClientToAdd('');
      await fetchGroups(user.id);
    } catch (err: any) {
      console.error('[USER DETAIL] Error adding user to client group:', err);
      setError(errorText(err, t('users.detail.errGroupAdd')));
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const removeClientGroup = async (groupId: string, groupName: string) => {
    if (!user) return;
    if (!window.confirm(t('users.detail.confirmRemoveGroup', { group: groupName }))) return;
    setIsLoadingGroups(true);
    setError(null);
    try {
      await userService.removeUserFromGroup(user.id, groupId);
      setSuccess(t('users.detail.msgGroupRemoved'));
      await fetchGroups(user.id);
    } catch (err: any) {
      console.error('[USER DETAIL] Error removing user from client group:', err);
      setError(errorText(err, t('users.detail.errGroupRemove')));
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const addRole = async () => {
    if (!user || !selectedRoleToAdd) return;
    setIsLoadingRoles(true);
    setError(null);
    try {
      await userService.addClientRoleToUser(user.id, environmentClientId, selectedRoleToAdd);
      setSuccess(t('users.detail.msgRoleAdded', { role: selectedRoleToAdd }));
      setSelectedRoleToAdd('');
      await fetchRoles(user.id);
    } catch (err: any) {
      console.error('[USER DETAIL] Error adding role:', err);
      setError(errorText(err, t('users.detail.errRoleAdd')));
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const removeRole = async (roleName: string) => {
    if (!user) return;
    if (!canManageRole(roleName)) {
      setError(t('users.cantRemoveHigherRole'));
      return;
    }
    if (!window.confirm(t('users.detail.confirmRemoveRole', { role: roleName }))) return;
    setIsLoadingRoles(true);
    setError(null);
    try {
      await userService.removeClientRoleFromUser(user.id, environmentClientId, roleName);
      setSuccess(t('users.detail.msgRoleRemoved', { role: roleName }));
      await fetchRoles(user.id);
    } catch (err: any) {
      console.error('[USER DETAIL] Error removing role:', err);
      setError(errorText(err, t('users.detail.errRoleRemove')));
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const fillGeneratedPassword = () => {
    const generated = generatePassword();
    setNewPassword(generated);
    setConfirmPassword(generated);
    setShowPassword(true);
  };

  const canResetPassword = Boolean(newPassword && confirmPassword && newPassword === confirmPassword && newPassword.length >= 8) && !isLoading;

  const resetPassword = async () => {
    if (!user) return;
    if (newPassword !== confirmPassword) {
      setError(t('users.create.errPasswordMismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('users.create.errPasswordShort'));
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await userService.resetPassword(user.id, { password: newPassword, temporary: temporaryPassword });
      setSuccess(t('users.detail.msgPasswordReset'));
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      console.error('[USER DETAIL] Error resetting password:', err);
      setError(errorText(err, t('users.detail.errPassword')));
    } finally {
      setIsLoading(false);
    }
  };

  const close = () => {
    setIsEditing(false);
    setActiveTab('details');
    setError(null);
    setSuccess(null);
    setNewPassword('');
    setConfirmPassword('');
    onClose();
  };

  return {
    user, isOpen,
    // dialog
    activeTab, setActiveTab, isLoading, error, setError, success, setSuccess, close,
    canWriteUsers, showsInternals, isSuperAdmin, environmentClientId,
    // identity
    isEditing, setIsEditing, editEmail, setEditEmail, editFirstName, setEditFirstName, editLastName, setEditLastName,
    editEnabled, setEditEnabled, editEmailVerified, setEditEmailVerified, saveIdentity, cancelEdit, deleteUser,
    // groups
    groups, clientNames, allClients, availableClientsToAdd, selectedClientToAdd, setSelectedClientToAdd, isLoadingGroups, addClientGroup, removeClientGroup,
    // roles
    clientRoles, availableRoles, selectedRoleToAdd, setSelectedRoleToAdd, isLoadingRoles, addRole, removeRole, canManageRole, roleDescription,
    // password
    newPassword, setNewPassword, confirmPassword, setConfirmPassword, temporaryPassword, setTemporaryPassword, showPassword, setShowPassword,
    fillGeneratedPassword, canResetPassword, resetPassword,
  };
};

export type UserDetailState = ReturnType<typeof useUserDetail>;
